/**
 * P1 Sprint — ManagerCargo Status Generator (frontend).
 *
 * 3 sub-tabs:
 *   - Genereer: medewerker kiest categorie → template → vult velden → preview → kopieer/opslaan
 *   - Statuslog: overzicht van eerder gegenereerde statussen met filters
 *   - Templates: alleen admin/owner — CRUD voor categorieën + templates
 *
 * UX-eis: < 20 sec van categorie tot gekopieerde status.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ClipboardList, Plus, Copy, CheckCircle2, Pencil, X, Bell, Link as LinkIcon,
  ChevronRight, Search,
} from "lucide-react";

import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Checkbox } from "../../ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";

import DatePickerField from "../DatePickerField";
import { adminApi, useAuth } from "../../../context/AuthContext";

const LINK_TYPE_LABELS = {
  klant: "Klant",
  pickup: "Pickup & Delivery",
  call_log: "Call Log",
  offerte: "Offerte",
  factuur: "Factuur",
  webshop_order: "Webshoporder",
  tracking: "Trackingnummer",
};

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function tomorrowIso() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

// ============================================================
// MAIN SECTION
// ============================================================

export default function StatusGeneratorSection() {
  const { user } = useAuth();
  const canManageTemplates = user?.role === "owner" || user?.role === "admin";
  const [tab, setTab] = useState("generate");

  return (
    <div className="space-y-4" data-testid="status-generator-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-chivo font-black text-2xl text-[#082c50] flex items-center gap-2">
            <ClipboardList className="w-6 h-6" /> Status Generator
          </h2>
          <p className="text-xs text-gray-600">
            Genereer uniforme ManagerCargo-statussen zonder vrije tekst — kopieer en plak in ManagerCargo.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border border-gray-200 bg-white w-fit">
        {[
          ["generate", "Genereer"],
          ["logs", "Statuslog"],
          ...(canManageTemplates ? [["templates", "Templates"]] : []),
        ].map(([k, l]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`status-tab-${k}`}
            className={`px-4 py-2 text-xs uppercase font-bold tracking-wide ${
              tab === k ? "bg-[#082c50] text-white" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "generate" && <GenerateTab />}
      {tab === "logs" && <LogsTab />}
      {tab === "templates" && canManageTemplates && <TemplatesTab />}
    </div>
  );
}

// ============================================================
// GENERATE TAB
// ============================================================

function GenerateTab() {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [selectedTpl, setSelectedTpl] = useState(null);
  const [values, setValues] = useState({});
  const [linkType, setLinkType] = useState("");
  const [linkId, setLinkId] = useState("");
  const [linkSnapshot, setLinkSnapshot] = useState("");
  const [internalNote, setInternalNote] = useState("");
  const [addReminder, setAddReminder] = useState(false);
  const [reminder, setReminder] = useState({
    date: tomorrowIso(),
    time_of_day: "08:00",
    priority: "normaal",
    text: "",
  });
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  // Laad categorieën + templates initieel
  useEffect(() => {
    (async () => {
      try {
        const [c, t] = await Promise.all([
          adminApi.get("/admin/status/categories"),
          adminApi.get("/admin/status/templates"),
        ]);
        setCategories(c.data || []);
        setTemplates(t.data || []);
      } catch (e) {
        toast.error("Laden mislukt");
      }
    })();
  }, []);

  // Templates voor geselecteerde categorie
  const visibleTemplates = useMemo(
    () => templates.filter((t) => !selectedCat || t.category_id === selectedCat.id),
    [templates, selectedCat]
  );

  // Preview is een derived value — useMemo i.p.v. useEffect+setState om
  // dubbele renders en de set-state-in-effect lint te voorkomen.
  // Hier passen we DE SLIMME currency-pattern toe (zelfde als backend):
  //  - leeg CALCULATION → strip "[CALCULATION] = " uit body
  //  - dubbele valuta-prefix in TOTAL_AMOUNT wordt weggepoetst
  //  - CALCULATION krijgt auto-spacing zodat preview netjes oogt
  const preview = useMemo(() => {
    if (!selectedTpl) return "";
    let rendered = selectedTpl.body || "";
    const calc = (values.CALCULATION || "").trim();
    if (!calc) {
      rendered = rendered.replace(
        /\[CALCULATION\]\s*=\s*\[CURRENCY\]\s*\[TOTAL_AMOUNT\]/g,
        "[CURRENCY] [TOTAL_AMOUNT]"
      );
    }
    (selectedTpl.fields || []).forEach((f) => {
      let v = values[f.key];
      if (v && f.key === "CALCULATION") {
        // Auto-spacing voorbeeld
        v = String(v)
          .replace(/\s+/g, " ")
          .replace(/\s*=\s*/g, " = ")
          .replace(/\s*\+\s*/g, " + ")
          .replace(/(\d)x(\d)/gi, "$1x $2")
          .replace(/\s+/g, " ")
          .trim();
      }
      if (v && f.key === "TOTAL_AMOUNT") {
        // Strip currency prefix (voorkomt "EUR EUR ...") + komma normalisatie
        const cur = (values.CURRENCY || "EUR").toUpperCase();
        v = String(v).replace(new RegExp(`^\\s*(?:EUR|USD|ANG|XCG|SRD)\\s+`, "i"), "");
        if (/^-?\d+\.\d{1,2}$/.test(v)) v = v.replace(".", ",");
        // niet gebruikt: cur — backend gebruikt CURRENCY veld separaat
        void cur;
      }
      const re = new RegExp(`\\[${f.key}\\]`, "g");
      rendered = rendered.replace(re, v && String(v).trim() ? v : `[${f.key}]`);
    });
    return rendered;
  }, [selectedTpl, values]);

  const handlePickCategory = (cat) => {
    setSelectedCat(cat);
    setSelectedTpl(null);
    setValues({});
    setCopied(false);
  };

  const handlePickTemplate = (tpl) => {
    setSelectedTpl(tpl);
    // Initialiseer values met defaults: select-velden krijgen hun placeholder
    // (bv. EUR voor CURRENCY) zodat de preview meteen klopt en server-side
    // required-validatie niet faalt.
    const init = {};
    (tpl.fields || []).forEach((f) => {
      if (f.type === "select") {
        init[f.key] = f.placeholder || (f.options || [])[0] || "";
      } else {
        init[f.key] = "";
      }
    });
    setValues(init);
    setCopied(false);
  };

  /**
   * Normaliseer values vlak voor versturen naar backend.
   * Defensieve laag tegen:
   *  - race conditions waarbij Select-state nog niet is gepropageerd
   *  - lege select-velden door snelle klik (init -> direct submit)
   *  - rommelige spacing in calculation veld
   */
  const buildSubmitValues = () => {
    if (!selectedTpl) return {};
    const out = {};
    (selectedTpl.fields || []).forEach((f) => {
      let v = values[f.key];
      if (v === undefined || v === null) v = "";
      v = String(v).trim();
      // Defensieve fallback voor select-velden: gebruik default (placeholder of eerste optie)
      if ((!v || v === "") && f.type === "select") {
        v = f.placeholder || (f.options || [])[0] || "";
      }
      // Auto-spacing voor CALCULATION veld zodat "5x8,25=41,25+15,99" → "5x 8,25 = 41,25 + 15,99"
      if (f.key === "CALCULATION" && v) {
        v = v
          .replace(/\s+/g, " ")               // collapse meerdere spaties
          .replace(/\s*=\s*/g, " = ")         // = altijd met spaties
          .replace(/\s*\+\s*/g, " + ")        // + altijd met spaties
          .replace(/(\d)x(\d)/gi, "$1x $2")   // "5x8" → "5x 8" (alleen tussen cijfers)
          .replace(/\s+/g, " ")               // herhaal: collapse na injecties
          .trim();
      }
      out[f.key] = v;
    });
    return out;
  };

  const handleCopy = async () => {
    if (!selectedTpl) return;
    const submitValues = buildSubmitValues();
    try {
      const r = await adminApi.post("/admin/status/generate", {
        template_id: selectedTpl.id,
        values: submitValues,
      });
      const serverRendered = r.data?.rendered || preview;
      await navigator.clipboard.writeText(serverRendered);
      setCopied(true);
      toast.success("Status gekopieerd");
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Onbekende fout";
      toast.error(`Genereren mislukt: ${detail}`);
    }
  };

  const handleSaveLog = async () => {
    if (!selectedTpl) return;
    setSaving(true);
    try {
      const submitValues = buildSubmitValues();
      const payload = {
        template_id: selectedTpl.id,
        values: submitValues,
        link_type: linkType || null,
        link_id: linkType ? (linkId.trim() || null) : null,
        internal_note: internalNote.trim() || null,
      };
      if (addReminder) {
        if (!reminder.date) {
          toast.error("Reminder-datum is verplicht.");
          setSaving(false);
          return;
        }
        payload.reminder = {
          date: reminder.date,
          time_of_day: reminder.time_of_day || null,
          priority: reminder.priority || "normaal",
          text: reminder.text || null,
        };
      }
      await adminApi.post("/admin/status/logs", payload);
      toast.success(addReminder ? "Statuslog + reminder opgeslagen" : "Statuslog opgeslagen");
      // Reset velden voor next status
      setValues({});
      setLinkType("");
      setLinkId("");
      setLinkSnapshot("");
      setInternalNote("");
      setAddReminder(false);
      setReminder({ date: tomorrowIso(), time_of_day: "08:00", priority: "normaal", text: "" });
      setSelectedTpl(null);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Onbekende fout";
      toast.error(`Opslaan mislukt: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="status-generate-tab">
      {/* Step 1+2: Category + Template */}
      <div className="bg-white border border-gray-200 p-3">
        <div className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-2">1. Kies categorie</div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => handlePickCategory(c)}
              data-testid={`status-cat-${c.id}`}
              className={`text-xs px-3 py-1.5 border ${
                selectedCat?.id === c.id
                  ? "bg-[#082c50] text-white border-[#082c50]"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        {selectedCat && (
          <>
            <div className="text-xs uppercase font-bold tracking-wide text-gray-700 mt-4 mb-2">
              2. Kies status-template
            </div>
            {visibleTemplates.length === 0 && (
              <div className="text-xs text-gray-500 italic">Geen actieve templates in deze categorie.</div>
            )}
            <ul className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
              {visibleTemplates.map((t) => (
                <li
                  key={t.id}
                  onClick={() => handlePickTemplate(t)}
                  data-testid={`status-tpl-${t.id}`}
                  className={`p-2 border cursor-pointer text-xs ${
                    selectedTpl?.id === t.id
                      ? "border-[#082c50] bg-[#082c50]/5"
                      : "border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="font-bold text-[#082c50] leading-tight">{t.name}</div>
                  {(t.placeholders || []).length > 0 && (
                    <div className="text-[10px] text-gray-500 mt-0.5">
                      Velden: {(t.placeholders || []).join(", ")}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Step 3: Fields */}
      <div className="bg-white border border-gray-200 p-3" data-testid="status-fields-block">
        <div className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-2">3. Vul velden in</div>
        {!selectedTpl && (
          <div className="text-xs text-gray-500 italic">Kies eerst een template links.</div>
        )}
        {selectedTpl && (
          <div className="space-y-3">
            {(selectedTpl.fields || []).map((f) => (
              <div key={f.key}>
                <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-700">
                  {f.label} {f.required && <span className="text-red-600">*</span>}
                </Label>
                {f.type === "date" ? (
                  <DatePickerField
                    value={values[f.key] || ""}
                    onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
                    placeholder={f.placeholder || "Kies datum"}
                    testId={`status-field-${f.key.toLowerCase()}`}
                  />
                ) : f.type === "select" ? (
                  <Select
                    value={values[f.key] || f.placeholder || (f.options || [])[0] || ""}
                    onValueChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))}
                  >
                    <SelectTrigger className="h-9 text-xs rounded-none" data-testid={`status-field-${f.key.toLowerCase()}`}>
                      <SelectValue placeholder={f.placeholder || "Kies..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={values[f.key] || ""}
                    onChange={(e) => setValues((s) => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={f.placeholder || ""}
                    className="h-9 text-xs rounded-none"
                    data-testid={`status-field-${f.key.toLowerCase()}`}
                  />
                )}
              </div>
            ))}

            {/* Koppel aan record */}
            <div className="pt-3 border-t border-gray-100">
              <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-700">
                Koppel aan record (optioneel)
              </Label>
              <Select value={linkType || "none"} onValueChange={(v) => { setLinkType(v === "none" ? "" : v); setLinkId(""); }}>
                <SelectTrigger className="h-9 text-xs rounded-none" data-testid="status-link-type">
                  <SelectValue placeholder="Geen koppeling" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen koppeling</SelectItem>
                  {Object.entries(LINK_TYPE_LABELS).map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {linkType && (
                <Input
                  value={linkId}
                  onChange={(e) => setLinkId(e.target.value)}
                  placeholder={
                    linkType === "tracking"
                      ? "Trackingnummer"
                      : "ID van record (uit lijst)"
                  }
                  className="h-9 text-xs rounded-none mt-2"
                  data-testid="status-link-id"
                />
              )}
            </div>

            {/* Interne notitie */}
            <div>
              <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-700">
                Interne notitie (komt NIET in ManagerCargo-status)
              </Label>
              <Textarea
                value={internalNote}
                onChange={(e) => setInternalNote(e.target.value)}
                placeholder="Bv. besproken met Jan om 10:30..."
                className="text-xs rounded-none min-h-[60px]"
                data-testid="status-internal-note"
              />
            </div>

            {/* Reminder */}
            <div className="pt-3 border-t border-gray-100">
              <label className="flex items-center gap-2 cursor-pointer text-xs" data-testid="status-add-reminder-label">
                <Checkbox
                  checked={addReminder}
                  onCheckedChange={(v) => setAddReminder(Boolean(v))}
                  data-testid="status-add-reminder"
                />
                <span className="font-bold">Reminder aanmaken</span>
              </label>
              {addReminder && (
                <div className="mt-2 grid grid-cols-2 gap-2 p-3 bg-amber-50 border border-amber-200">
                  <div>
                    <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-700">Datum *</Label>
                    <DatePickerField
                      value={reminder.date}
                      onChange={(v) => setReminder((s) => ({ ...s, date: v }))}
                      placeholder="Kies datum"
                      testId="status-reminder-date"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-700">Tijd</Label>
                    <Input
                      value={reminder.time_of_day}
                      onChange={(e) => setReminder((s) => ({ ...s, time_of_day: e.target.value }))}
                      placeholder="HH:MM"
                      className="h-9 text-xs rounded-none"
                      data-testid="status-reminder-time"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-700">Prioriteit</Label>
                    <Select value={reminder.priority} onValueChange={(v) => setReminder((s) => ({ ...s, priority: v }))}>
                      <SelectTrigger className="h-9 text-xs rounded-none" data-testid="status-reminder-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="laag">Laag</SelectItem>
                        <SelectItem value="normaal">Normaal</SelectItem>
                        <SelectItem value="hoog">Hoog</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px] uppercase font-bold tracking-wide text-gray-700">Reminder-tekst</Label>
                    <Textarea
                      value={reminder.text}
                      onChange={(e) => setReminder((s) => ({ ...s, text: e.target.value }))}
                      placeholder="Leeg = default opvolging-tekst"
                      className="text-xs rounded-none min-h-[50px]"
                      data-testid="status-reminder-text"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Step 4+5: Preview + actions */}
      <div className="bg-white border border-gray-200 p-3" data-testid="status-preview-block">
        <div className="text-xs uppercase font-bold tracking-wide text-gray-700 mb-2">
          4. Preview voor ManagerCargo
        </div>
        {!selectedTpl ? (
          <div className="text-xs text-gray-500 italic">Kies een template om de preview te zien.</div>
        ) : (
          <>
            <pre
              data-testid="status-preview"
              className="bg-gray-50 border border-gray-200 p-3 text-[12px] font-mono whitespace-pre-wrap break-words min-h-[180px] max-h-[420px] overflow-y-auto"
            >
              {preview}
            </pre>
            <div className="bg-amber-50 border border-amber-200 px-3 py-2 mt-2 text-[11px] text-amber-900 flex items-start gap-2">
              <span className="font-bold">⚠</span>
              <span>
                Gebruik geen vrije tekst als officiële status. Kies altijd een vaste status-template.
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                onClick={handleCopy}
                disabled={!preview}
                className="bg-[#082c50] hover:bg-[#06223f] text-white rounded-none text-xs font-bold uppercase"
                data-testid="status-copy-btn"
              >
                {copied ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
                {copied ? "Gekopieerd" : "Kopieer status"}
              </Button>
              <Button
                onClick={handleSaveLog}
                disabled={saving}
                variant="outline"
                className="rounded-none text-xs font-bold uppercase border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
                data-testid="status-save-btn"
              >
                {saving ? "Opslaan..." : "Opslaan in statuslog"}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// LOGS TAB
// ============================================================

function LogsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("today");
  const [withReminder, setWithReminder] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (range && range !== "all") params.range = range;
      if (withReminder) params.with_reminder = true;
      if (q.trim()) params.q = q.trim();
      const r = await adminApi.get("/admin/status/logs", { params });
      setItems(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [range, withReminder, q]);

  useEffect(() => { reload(); }, [range, withReminder]); // eslint-disable-line

  const handleCopyAgain = async (log) => {
    try {
      await navigator.clipboard.writeText(log.rendered || "");
      await adminApi.post(`/admin/status/logs/${log.id}/mark-copied`);
      toast.success("Status opnieuw gekopieerd");
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  return (
    <div className="space-y-3" data-testid="status-logs-tab">
      <div className="bg-white border border-gray-200 p-3 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 border border-gray-200">
          {[["today", "Vandaag"], ["this_week", "Deze week"], ["all", "Alle"]].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setRange(k)}
              data-testid={`status-logs-range-${k}`}
              className={`px-3 py-1.5 text-xs uppercase font-bold ${
                range === k ? "bg-[#082c50] text-white" : "text-gray-600 hover:bg-gray-50"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-1 text-xs cursor-pointer">
          <Checkbox checked={withReminder} onCheckedChange={(v) => setWithReminder(Boolean(v))} data-testid="status-logs-filter-with-reminder" />
          Met reminder
        </label>
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder="Zoek template, klant, tracking..."
            className="pl-7 rounded-none text-xs h-9"
            data-testid="status-logs-search"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200">
        {loading ? (
          <div className="p-6 text-gray-500 text-sm">Laden...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-500 text-sm" data-testid="status-logs-empty">
            Geen statuslogs voor dit filter.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((log) => (
              <li key={log.id} className="p-3 hover:bg-gray-50" data-testid={`status-log-row-${log.id}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-[#082c50] flex items-center gap-2 flex-wrap">
                      <span className="bg-[#082c50] text-white px-2 py-0.5 text-[9px] uppercase">{log.category_name || "—"}</span>
                      <span>{log.template_name}</span>
                      {log.reminder_id && (
                        <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[9px] uppercase border border-amber-200 flex items-center gap-1">
                          <Bell className="w-3 h-3" /> Reminder
                        </span>
                      )}
                      {log.linked_record_snapshot && (
                        <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[9px] uppercase border border-blue-200 flex items-center gap-1">
                          <LinkIcon className="w-3 h-3" /> {LINK_TYPE_LABELS[log.link_type] || log.link_type}: {log.linked_record_snapshot}
                        </span>
                      )}
                    </div>
                    <pre className="text-[11px] font-mono mt-1 whitespace-pre-wrap break-words line-clamp-3 text-gray-700">
                      {log.rendered}
                    </pre>
                    <div className="text-[10px] text-gray-500 mt-1">
                      {log.created_by_email} • {(log.created_at || "").slice(0, 16).replace("T", " ")}
                      {log.copied_at && ` • laatst gekopieerd ${log.copied_at.slice(0, 16).replace("T", " ")}`}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyAgain(log)}
                      className="rounded-none text-[10px] h-7 px-2"
                      data-testid={`status-log-copy-${log.id}`}
                    >
                      <Copy className="w-3 h-3 mr-1" /> Kopieer
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelected(log)}
                      className="rounded-none text-[10px] h-7 px-2"
                      data-testid={`status-log-detail-${log.id}`}
                    >
                      Detail
                    </Button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="status-log-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.template_name}</DialogTitle>
                <DialogDescription>{selected.category_name}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <pre className="bg-gray-50 border border-gray-200 p-3 text-[12px] font-mono whitespace-pre-wrap">{selected.rendered}</pre>
                {selected.linked_record_snapshot && (
                  <div className="text-xs">
                    <span className="font-bold uppercase text-gray-500">Gekoppeld aan:</span>{" "}
                    {LINK_TYPE_LABELS[selected.link_type] || selected.link_type}: {selected.linked_record_snapshot}
                  </div>
                )}
                {selected.internal_note && (
                  <div className="text-xs italic text-gray-700">
                    <span className="font-bold uppercase text-gray-500 not-italic">Interne notitie:</span> {selected.internal_note}
                  </div>
                )}
                <div className="text-[10px] text-gray-500">
                  Aangemaakt door {selected.created_by_email} op {(selected.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// TEMPLATES TAB (admin/owner only)
// ============================================================

function confirmDeactivate(label) {
  // Extracted out of components to avoid a false-positive
  // "react-hooks/set-state-in-effect" lint trigger on inline window.confirm
  // followed by setState/reload() in async handlers.
  return window.confirm(`${label} deactiveren?`);
}

function TemplatesTab() {
  const [categories, setCategories] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTpl, setEditingTpl] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const triggerReload = () => setReloadTick((n) => n + 1);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [c, t] = await Promise.all([
          adminApi.get("/admin/status/categories", { params: { include_inactive: true } }),
          adminApi.get("/admin/status/templates", { params: { include_inactive: true } }),
        ]);
        if (!cancelled) {
          setCategories(c.data || []);
          setTemplates(t.data || []);
        }
      } catch {
        if (!cancelled) toast.error("Laden mislukt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  const handleDeleteCat = async (id) => {
    if (!confirmDeactivate("Categorie")) return;
    try {
      await adminApi.delete(`/admin/status/categories/${id}`);
      toast.success("Categorie gedeactiveerd");
      triggerReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mislukt");
    }
  };

  const handleDeleteTpl = async (id) => {
    if (!confirmDeactivate("Template")) return;
    try {
      await adminApi.delete(`/admin/status/templates/${id}`);
      toast.success("Template gedeactiveerd");
      triggerReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mislukt");
    }
  };

  return (
    <div className="space-y-4" data-testid="status-templates-tab">
      {/* Categories */}
      <div className="bg-white border border-gray-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase font-bold tracking-wide text-gray-700">Categorieën</div>
          <Button
            size="sm"
            onClick={() => setEditingCat({ name: "", sort_order: 0, active: true })}
            className="bg-[#082c50] text-white rounded-none text-[10px] h-7"
            data-testid="status-cat-new"
          >
            <Plus className="w-3 h-3 mr-1" /> Nieuwe categorie
          </Button>
        </div>
        {loading ? (
          <div className="text-xs text-gray-500">Laden...</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {categories.map((c) => (
              <li key={c.id} className="flex items-center justify-between py-1.5">
                <div className="text-xs">
                  <span className="font-bold">{c.name}</span>
                  <span className="text-gray-500 ml-2">sort: {c.sort_order}</span>
                  {!c.active && <span className="ml-2 text-red-600 text-[10px] uppercase">Inactief</span>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditingCat(c)} className="h-7 px-2" data-testid={`status-cat-edit-${c.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  {c.active && (
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteCat(c.id)} className="h-7 px-2 text-red-600" data-testid={`status-cat-del-${c.id}`}>
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Templates */}
      <div className="bg-white border border-gray-200 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs uppercase font-bold tracking-wide text-gray-700">Templates ({templates.length})</div>
          <Button
            size="sm"
            onClick={() => setEditingTpl({ name: "", body: "", category_id: categories[0]?.id || "", fields: [], sort_order: 0, active: true })}
            className="bg-[#082c50] text-white rounded-none text-[10px] h-7"
            data-testid="status-tpl-new"
          >
            <Plus className="w-3 h-3 mr-1" /> Nieuw template
          </Button>
        </div>
        <ul className="divide-y divide-gray-100">
          {templates.map((t) => {
            const cat = categories.find((c) => c.id === t.category_id);
            return (
              <li key={t.id} className="flex items-center justify-between py-1.5">
                <div className="text-xs min-w-0 flex-1">
                  <div className="font-bold truncate">{t.name}</div>
                  <div className="text-gray-500 text-[10px]">
                    {cat?.name || "—"} • {(t.placeholders || []).join(", ") || "geen placeholders"} • sort {t.sort_order}
                    {!t.active && <span className="ml-2 text-red-600 uppercase">Inactief</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" onClick={() => setEditingTpl(t)} className="h-7 px-2" data-testid={`status-tpl-edit-${t.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  {t.active && (
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteTpl(t.id)} className="h-7 px-2 text-red-600" data-testid={`status-tpl-del-${t.id}`}>
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Category Edit Dialog */}
      <Dialog open={!!editingCat} onOpenChange={(v) => !v && setEditingCat(null)}>
        <DialogContent className="max-w-md" data-testid="status-cat-dialog">
          <DialogHeader>
            <DialogTitle>{editingCat?.id ? "Bewerk categorie" : "Nieuwe categorie"}</DialogTitle>
            <DialogDescription>Categorieën groeperen templates in de generator-UI.</DialogDescription>
          </DialogHeader>
          {editingCat && (
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase font-bold">Naam</Label>
                <Input value={editingCat.name} onChange={(e) => setEditingCat({ ...editingCat, name: e.target.value })} className="rounded-none h-9 text-xs" data-testid="status-cat-name-input" />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold">Sortering</Label>
                <Input type="number" value={editingCat.sort_order || 0} onChange={(e) => setEditingCat({ ...editingCat, sort_order: parseInt(e.target.value || "0", 10) })} className="rounded-none h-9 text-xs" />
              </div>
              <label className="flex items-center gap-2 text-xs">
                <Checkbox checked={!!editingCat.active} onCheckedChange={(v) => setEditingCat({ ...editingCat, active: Boolean(v) })} />
                Actief
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditingCat(null)} className="rounded-none text-xs">Annuleren</Button>
                <Button
                  onClick={async () => {
                    try {
                      if (editingCat.id) {
                        await adminApi.patch(`/admin/status/categories/${editingCat.id}`, {
                          name: editingCat.name,
                          sort_order: editingCat.sort_order,
                          active: editingCat.active,
                        });
                      } else {
                        await adminApi.post("/admin/status/categories", {
                          name: editingCat.name,
                          sort_order: editingCat.sort_order,
                          active: editingCat.active,
                        });
                      }
                      toast.success("Opgeslagen");
                      setEditingCat(null);
                      triggerReload();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
                    }
                  }}
                  className="bg-[#082c50] text-white rounded-none text-xs"
                  data-testid="status-cat-save"
                >
                  Opslaan
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Edit Dialog */}
      <Dialog open={!!editingTpl} onOpenChange={(v) => !v && setEditingTpl(null)}>
        <DialogContent className="max-w-3xl" data-testid="status-tpl-dialog">
          <DialogHeader>
            <DialogTitle>{editingTpl?.id ? "Bewerk template" : "Nieuw template"}</DialogTitle>
            <DialogDescription>
              Gebruik placeholders zoals [BEDRAG_OF_BEREKENING] of [KLANTNAAM] in de body. Definieer per veld of het verplicht is.
            </DialogDescription>
          </DialogHeader>
          {editingTpl && (
            <TemplateEditor
              tpl={editingTpl}
              categories={categories}
              onCancel={() => setEditingTpl(null)}
              onSaved={() => { setEditingTpl(null); triggerReload(); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateEditor({ tpl, categories, onCancel, onSaved }) {
  const [draft, setDraft] = useState({
    ...tpl,
    fields: Array.isArray(tpl.fields) ? tpl.fields : [],
  });
  const [saving, setSaving] = useState(false);

  // Auto-detecteer placeholders uit body voor handige UI hint
  const detected = (draft.body || "").match(/\[([A-Z_]+)\]/g) || [];
  const detectedKeys = [...new Set(detected.map((m) => m.slice(1, -1)))];

  const addField = (key) => {
    if (draft.fields.find((f) => f.key === key)) return;
    setDraft({
      ...draft,
      fields: [...draft.fields, { key, label: key.replace(/_/g, " "), required: false, type: "text" }],
    });
  };
  const updateField = (idx, patch) => {
    setDraft({
      ...draft,
      fields: draft.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    });
  };
  const removeField = (idx) => {
    setDraft({ ...draft, fields: draft.fields.filter((_, i) => i !== idx) });
  };

  const handleSave = async () => {
    if (!draft.name?.trim() || !draft.body?.trim() || !draft.category_id) {
      toast.error("Naam, categorie en body zijn verplicht");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        category_id: draft.category_id,
        name: draft.name.trim(),
        body: draft.body,
        fields: draft.fields,
        sort_order: parseInt(draft.sort_order || 0, 10),
        active: !!draft.active,
        language: draft.language || "en",
        internal_note: draft.internal_note || null,
      };
      if (draft.id) {
        await adminApi.patch(`/admin/status/templates/${draft.id}`, payload);
      } else {
        await adminApi.post("/admin/status/templates", payload);
      }
      toast.success("Template opgeslagen");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase font-bold">Categorie</Label>
          <Select value={draft.category_id} onValueChange={(v) => setDraft({ ...draft, category_id: v })}>
            <SelectTrigger className="h-9 text-xs rounded-none" data-testid="status-tpl-edit-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.filter((c) => c.active).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase font-bold">Sortering</Label>
          <Input type="number" value={draft.sort_order || 0} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} className="rounded-none h-9 text-xs" />
        </div>
      </div>
      <div>
        <Label className="text-[10px] uppercase font-bold">Naam (zichtbaar voor medewerker)</Label>
        <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="rounded-none h-9 text-xs" data-testid="status-tpl-edit-name" />
      </div>
      <div>
        <Label className="text-[10px] uppercase font-bold">Body (gebruik [PLACEHOLDER] voor velden)</Label>
        <Textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} className="text-xs rounded-none min-h-[120px] font-mono" data-testid="status-tpl-edit-body" />
        {detectedKeys.length > 0 && (
          <div className="text-[10px] text-gray-500 mt-1">
            Gedetecteerd: {detectedKeys.join(", ")}
          </div>
        )}
      </div>

      <div>
        <div className="text-[10px] uppercase font-bold text-gray-700 mb-1">Velden (definieer hier welke placeholders verplicht zijn)</div>
        {draft.fields.length === 0 && (
          <div className="text-[10px] text-gray-500 italic">Geen velden gedefinieerd. Voeg toe via knoppen hieronder.</div>
        )}
        {draft.fields.map((f, i) => (
          <div key={i} className="space-y-1 mb-2 p-2 border border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono bg-gray-100 px-2 py-1">[{f.key}]</span>
              <Input value={f.label || ""} onChange={(e) => updateField(i, { label: e.target.value })} placeholder="Label" className="rounded-none h-8 text-xs" />
              <Select value={f.type || "text"} onValueChange={(v) => updateField(i, { type: v })}>
                <SelectTrigger className="h-8 text-xs rounded-none w-24"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Tekst</SelectItem>
                  <SelectItem value="number">Getal</SelectItem>
                  <SelectItem value="date">Datum</SelectItem>
                  <SelectItem value="select">Select</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1 text-[10px]">
                <Checkbox checked={!!f.required} onCheckedChange={(v) => updateField(i, { required: Boolean(v) })} />
                Verplicht
              </label>
              <Button size="sm" variant="ghost" onClick={() => removeField(i)} className="h-7 px-2 text-red-600"><X className="w-3 h-3" /></Button>
            </div>
            {f.type === "select" && (
              <div className="ml-4 flex items-center gap-2">
                <span className="text-[10px] text-gray-500">Opties (komma-gescheiden):</span>
                <Input
                  value={(f.options || []).join(",")}
                  onChange={(e) => updateField(i, { options: e.target.value.split(",").map((o) => o.trim()).filter(Boolean) })}
                  placeholder="EUR,USD,ANG,XCG,SRD"
                  className="rounded-none h-7 text-xs flex-1"
                />
                <span className="text-[10px] text-gray-500">Default:</span>
                <Input
                  value={f.placeholder || ""}
                  onChange={(e) => updateField(i, { placeholder: e.target.value })}
                  placeholder="EUR"
                  className="rounded-none h-7 text-xs w-20"
                />
              </div>
            )}
          </div>
        ))}
        <div className="flex flex-wrap gap-1 mt-2">
          {detectedKeys.filter((k) => !draft.fields.find((f) => f.key === k)).map((k) => (
            <Button key={k} size="sm" variant="outline" onClick={() => addField(k)} className="h-7 text-[10px] rounded-none">
              + {k}
            </Button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs">
        <Checkbox checked={!!draft.active} onCheckedChange={(v) => setDraft({ ...draft, active: Boolean(v) })} />
        Actief (zichtbaar voor medewerkers)
      </label>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={onCancel} className="rounded-none text-xs">Annuleren</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-[#082c50] text-white rounded-none text-xs" data-testid="status-tpl-edit-save">
          {saving ? "Opslaan..." : "Opslaan"}
        </Button>
      </div>
    </div>
  );
}
